import { Table, TableColumns } from '@sixfold/common-ui';
import React from 'react';
import { NavLink } from 'react-router';

import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { capitalize } from '../../lib/format';
import { CompanyInvitationsQuery, Maybe, UserProfile } from '../../lib/graphql';
import { Routes } from '../../routes';

export type InvitationsViewProps = Pick<CompanyInvitationsQuery, 'companyInvitations'>;

type CompanyInvitationProps = CompanyInvitationsQuery['companyInvitations'][0] & {
  invitedByUser: Maybe<Partial<UserProfile>>;
};

const getCompletionTime = (
  historyLog: CompanyInvitationsQuery['companyInvitations'][0]['historyLog'],
): string | null => {
  const acceptedEvent = historyLog.find((item) => item.type === 'ACCEPTED');
  return acceptedEvent?.time || null;
};

type InvitationRow = {
  id: string;
  createdAt: CompanyInvitationProps['invitedAt'];
  inviterCompany: CompanyInvitationProps['invitedByCompany'];
  status: string;
  inviterPerson: string;
  locale: string;
  email: string;
  aliasGiven: string | null;
  inviteMethod: string;
  completedAt: string | null;
};

const invitationColumns: TableColumns<InvitationRow> = [
  {
    id: 'createdAt',
    title: 'Created at',
    cellContent: ({ value }) => <FormattedDate date={value} />,
  },
  {
    id: 'inviterCompany',
    title: 'Inviter company',
    cellContent: ({ value }) =>
      value?.company_id ? (
        <NavLink to={Routes.Company.generatePath({ company_id: value.company_id })} className="item">
          {value.company_name}
        </NavLink>
      ) : (
        '-'
      ),
  },
  {
    id: 'status',
    title: 'Status',
  },
  {
    id: 'inviterPerson',
    title: 'Inviter person',
  },
  {
    id: 'locale',
    title: 'Locale',
  },
  {
    id: 'email',
    title: 'Invited email',
  },
  {
    id: 'aliasGiven',
    title: 'Alias given',
    cellContent: ({ value }) => value ?? '-',
  },
  {
    id: 'inviteMethod',
    title: 'Invite method',
  },
  {
    id: 'completedAt',
    title: 'Accepted',
    cellContent: ({ value }) => (value ? <FormattedDate date={value} /> : '-'),
  },
];

function buildRows(invitations: CompanyInvitationProps[]): InvitationRow[] {
  const rows: InvitationRow[] = [];

  for (const invitation of invitations) {
    const {
      companyInviteId,
      invitedAt,
      companyName,
      invitedByUser,
      inviteMethod,
      recipients,
      invitedByCompany,
      status,
      historyLog,
    } = invitation;

    const inviterPerson = invitedByUser
      ? `${invitedByUser.first_name ?? ''} ${invitedByUser.last_name ?? ''}`.trim()
      : '-';
    const completedAt = getCompletionTime(historyLog);
    const baseRow = {
      createdAt: invitedAt,
      inviterCompany: invitedByCompany,
      status: capitalize(status),
      inviterPerson,
      aliasGiven: companyName ?? null,
      inviteMethod: capitalize(inviteMethod ?? '-'),
      completedAt,
    };

    if (recipients.length === 0) {
      rows.push({ id: `${companyInviteId}-no-recipient`, ...baseRow, locale: '-', email: '-' });
    } else {
      recipients.forEach((recipient, idx) => {
        rows.push({
          id: `${companyInviteId}-${recipient.email}-${idx}`,
          ...baseRow,
          locale: recipient.locale,
          email: recipient.email,
        });
      });
    }
  }

  return rows;
}

export const CompanyInvitations: React.FC<InvitationsViewProps> = (props: InvitationsViewProps) => {
  const data = buildRows(props.companyInvitations as CompanyInvitationProps[]);

  return <div>{data.length === 0 ? <p>No invitations</p> : <Table data={data} columns={invitationColumns} />}</div>;
};
